import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button, Input, Spacer, Textarea } from "@nextui-org/react";
import { useTranslation } from "react-i18next";

import DefaultLayout from "@/layouts/default";
import { booksAPI } from "@/services/api"; // Import the books API service

// Book interface matching the database structure
interface Book {
  id: string;
  name: string;
  author: string;
  category: string;
  publisher: string;
  min_stocks: number;
  description?: string;
  language?: string;
  pageSize?: string;
  pageType?: string;
  pages?: string;
  publicationDate?: string;
}

const EditBookPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchBook = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!id) {
          setError("Book ID is missing");
          setLoading(false);

          return;
        }

        const bookData = await booksAPI.getById(id);

        // Format publication date from ISO format to YYYY-MM-DD for date input
        const formatDateForInput = (dateString: string | null | undefined): string => {
          if (!dateString) return "";
          try {
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
              // Format as YYYY-MM-DD for HTML date input
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
          } catch (error) {
            console.error("Error formatting date:", error);
          }
          return "";
        };

        // Transform the API response to ensure correct field mapping
        const formattedBook: Book = {
          ...bookData,
          // Handle different possible field name formats for min_stocks and ensure it's never null
          min_stocks: bookData.min_stocks || bookData.minStocks || 0,
          // Handle other possible field name variations
          pageSize: bookData.pageSize || bookData.page_size || "",
          pageType: bookData.pageType || bookData.page_type || "",
          publicationDate: formatDateForInput(
            bookData.publicationDate || bookData.publication_date
          ),
        };

        setBook(formattedBook);
        setLoading(false);
      } catch (error: any) {
        console.error("Error fetching book:", error);
        setError(`Failed to fetch book: ${error.message || "Unknown error"}`);
        setLoading(false);
      }
    };

    fetchBook();
  }, [id]);

  const handleChange = (field: string, value: string | number) => {
    setBook((prevBook) => {
      if (!prevBook) return null;

      // Special handling for numeric fields
      if (field === "min_stocks") {
        const numValue = parseInt(value as string) || 0;
        return { ...prevBook, [field]: numValue };
      }

      return { ...prevBook, [field]: value };
    });
  };

  const handleSave = async () => {
    if (book && id) {
      try {
        // Validate required fields first
        if (!book.name?.trim() || !book.author?.trim()) {
          alert(t("Name and Author are required fields"));
          return;
        }

        // Prepare the book data matching what the controller expects
        // Include required fields and optional fields (send empty string for optional fields if not provided)
        const bookData: any = {
          name: book.name.trim(),
          author: book.author.trim(),
          minStocks: Math.max(0, parseInt(String(book.min_stocks || 0)) || 0),
          category: book.category?.trim() || null,
        };
        // Add optional fields (send null if not provided to match backend expectations)
        bookData.publisher = book.publisher?.trim() || null;
        bookData.description = book.description?.trim() || null;
        bookData.language = book.language?.trim() || null;
        bookData.pageSize = book.pageSize?.trim() || null;
        bookData.pageType = book.pageType?.trim() || null;
        bookData.pages = book.pages?.trim() || null;
        
        // Handle publicationDate - validate and format if present
        // Date input already provides YYYY-MM-DD format, so we can use it directly
        if (book.publicationDate?.trim()) {
          const dateValue = book.publicationDate.trim();
          // Validate that it's in YYYY-MM-DD format
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (dateRegex.test(dateValue)) {
            bookData.publicationDate = dateValue;
          } else {
            // Fallback: try to parse and format if not in expected format
            const date = new Date(dateValue);
            if (!isNaN(date.getTime())) {
              bookData.publicationDate = date.toISOString().split('T')[0];
            } else {
              bookData.publicationDate = null;
            }
          }
        } else {
          bookData.publicationDate = null;
        }

        // Log the data being sent for debugging
        console.log("Updating book with data:", bookData);
        
        await booksAPI.update(id, bookData);

        alert(t("Book updated successfully!"));
        navigate("/bookList");
      } catch (error: any) {
        console.error("Error updating book:", error);
        console.error("Error response:", error.response?.data);
        console.error("Error status:", error.response?.status);
        
        // Extract more detailed error information
        let errorMessage = "Unknown error";
        
        if (error.response?.data) {
          // Try to get detailed error message
          if (error.response.data.details) {
            errorMessage = error.response.data.details;
          } else if (error.response.data.error) {
            errorMessage = typeof error.response.data.error === 'string' 
              ? error.response.data.error 
              : JSON.stringify(error.response.data.error);
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else {
            errorMessage = JSON.stringify(error.response.data);
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        alert(
          t("Failed to update book: ") + errorMessage
        );
      }
    }
  };

  if (loading) {
    return (
      <DefaultLayout>
        <div className="container mx-auto p-4">
          <p className="text-center">{t("Loading...")}</p>
        </div>
      </DefaultLayout>
    );
  }

  if (error) {
    return (
      <DefaultLayout>
        <div className="container mx-auto p-4">
          <div className="bg-red-500 text-white p-4 rounded mb-4">{error}</div>
          <Link className="text-blue-500 text-center mt-4 block" to="/bookList">
            {t("go_to_books_list")}
          </Link>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="container mx-auto p-4">
        <div className="row py-4">
          <h1 className="text-3xl text-center py-4 font-bold mb-4">
            {t("Edit Book")}
          </h1>
        </div>
        <div className="row">
          {book ? (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Input
                    label={t("Name")}
                    value={book.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                  />
                </div>
                <div>
                  <Input
                    label={t("Author")}
                    value={book.author}
                    onChange={(e) => handleChange("author", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Input
                    label={t("Category")}
                    value={book.category}
                    onChange={(e) => handleChange("category", e.target.value)}
                  />
                </div>
                <div>
                  <Input
                    label={t("Language")}
                    value={book.language || ""}
                    onChange={(e) => handleChange("language", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Input
                    label={t("Page Size")}
                    value={book.pageSize || ""}
                    onChange={(e) => handleChange("pageSize", e.target.value)}
                  />
                </div>
                <div>
                  <Input
                    label={t("Page Type")}
                    value={book.pageType || ""}
                    onChange={(e) => handleChange("pageType", e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Input
                    label={t("Pages")}
                    value={book.pages || ""}
                    onChange={(e) => handleChange("pages", e.target.value)}
                  />
                </div>
                <div>
                  <Input
                    label={t("Publication Date")}
                    type="date"
                    className="bg-white dark:bg-gray-800 text-black dark:text-white"
                    value={book.publicationDate || ""}
                    onChange={(e) =>
                      handleChange("publicationDate", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Input
                    label={t("Publisher")}
                    value={book.publisher || ""}
                    onChange={(e) => handleChange("publisher", e.target.value)}
                  />
                </div>
                <div>
                  <Input
                    label={t("Minimum Stock Limit")}
                    name="min_stocks"
                    type="number"
                    value={book.min_stocks?.toString() || "0"}
                    onChange={(e) => handleChange("min_stocks", parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className="mb-4">
                <Textarea
                  label={t("Description")}
                  rows={4}
                  value={book.description || ""}
                  onChange={(e) => handleChange("description", e.target.value)}
                />
              </div>
              <div className="flex justify-center gap-4">
                <Button color="primary" onClick={handleSave}>
                  {t("Save")}
                </Button>
              </div>
            </div>
          ) : (
            <p>{t("No book found")}</p>
          )}

          <Spacer y={1} />
          <Link className="text-blue-500 text-center mt-4 block" to="/bookList">
            {t("go_to_books_list")}
          </Link>
        </div>
      </div>
    </DefaultLayout>
  );
};

// Explicitly export as default and as named export to support both import styles
export { EditBookPage };
export default EditBookPage;
